import React from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Divider,
  Button,
} from "@heroui/react";
import Link from 'next/link';
import {
  BookOpenIcon,
  CalendarIcon,
  BookmarkAltIcon,
  LinkIcon,
  ClockIcon,
  GlobeIcon,
  LibraryIcon
} from '@heroicons/react/outline';

interface BookDetail {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  label: string;
  value: string | number | null;
}

interface BookDetailsProps {
  isbn: {
    ISBN13?: string | null;
    pageCount?: number | null;
    publisher?: string | null;
    publishedDate?: string | null;
    language?: string | null;
    slug?: string | null;
    id?: number | null;
  };
  createdAt?: string | null;
  className?: string;
}

/**
 * BookDetails - A reusable component for displaying book metadata in a card
 */
export default function BookDetails({
  isbn,
  createdAt,
  className = ""
}: BookDetailsProps) {
  // Create book details array
  const details: BookDetail[] = [
    {
      icon: BookmarkAltIcon,
      label: "ISBN",
      value: isbn?.ISBN13
    },
    {
      icon: BookOpenIcon,
      label: "Liczba stron",
      value: isbn?.pageCount
    },
    {
      icon: LibraryIcon,
      label: "Wydawnictwo",
      value: isbn?.publisher
    },
    {
      icon: CalendarIcon,
      label: "Data wydania",
      value: isbn?.publishedDate
    },
    {
      icon: GlobeIcon,
      label: "Język",
      value: isbn?.language
    }
  ];

  // Add creation date if available
  if (createdAt) {
    details.push({
      icon: ClockIcon,
      label: "Dodano",
      value: new Date(createdAt).toLocaleDateString()
    });
  }

  // Filter out null or undefined values
  const filteredDetails = details.filter(detail => detail.value != null);

  return (
    <Card className={`shadow-sm border border-gray-200 ${className}`}>
      <CardHeader className="pb-0 px-6">
        <h3 className="text-lg font-medium text-gray-900">Szczegóły książki</h3>
      </CardHeader>
      <CardBody className="px-6 py-4">
        <dl className="space-y-3">
          {filteredDetails.map((detail, index) => (
            <div key={index} className="flex justify-between">
              <dt className="flex items-center text-sm text-gray-600">
                <detail.icon className="mr-2 h-5 w-5 text-gray-400" />
                {detail.label}
              </dt>
              <dd className="text-sm font-medium text-gray-900">{detail.value}</dd>
            </div>
          ))}
        </dl>

        {(isbn?.slug || isbn?.id) && (
          <>
            <Divider className="my-4" />
            <div className="flex justify-center">
              <Button
                as={Link}
                href={`/book/${isbn?.slug || isbn?.id}`}
                size="sm"
                variant="flat"
                color="primary"
                endContent={<LinkIcon className="h-4 w-4" />}
              >
                Więcej o książce
              </Button>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}